import React from 'react';
import { Eye, Edit, Trash2, ChevronLeft, ChevronRight, Briefcase } from 'lucide-react';
import EmptyState from '../Common/EmptyState';
import Can from '../Common/Can';
import { P } from '../../constants/permissions';

const EconomicActivityTable = ({
  economicActivities,
  pagination,
  totalCount,
  onView,
  onEdit,
  onDelete,
  getStatusBadge,
  getExemptBadge,
  onCreate,
}) => {
  const total = totalCount != null ? totalCount : economicActivities?.length ?? 0;
  if (!economicActivities?.length) {
    return (
      <div className="card">
        <div className="card-content py-2">
          <EmptyState
            icon={Briefcase}
            title="Nenhuma atividade econômica cadastrada"
            description="Inclua CNAE ou atividades vinculadas a empreendimentos."
            action={
              onCreate ? (
                <Can permission={P.CATALOG_ACCESS}>
                  <button type="button" className="btn btn-primary" onClick={onCreate}>
                    Nova atividade
                  </button>
                </Can>
              ) : null
            }
          />
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-content p-0">
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>
                  <input type="checkbox" className="rounded border-gray-300" />
                </th>
                <th>Código</th>
                <th>Nome</th>
                <th>Descrição</th>
                <th>Tributação</th>
                <th>Status</th>
                <th>Criado em</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {economicActivities.map((economicActivity) => (
                <tr key={economicActivity.id}>
                  <td>
                    <input type="checkbox" className="rounded border-gray-300" />
                  </td>
                  <td>
                    <div className="font-medium text-gray-900">{economicActivity.code}</div>
                  </td>
                  <td>
                    <div className="font-medium text-gray-900">{economicActivity.name}</div>
                  </td>
                  <td>
                    <div className="text-sm text-gray-900 max-w-xs truncate">
                      {economicActivity.description}
                    </div>
                  </td>
                  <td>
                    {getExemptBadge(economicActivity.isExempt)}
                  </td>
                  <td>
                    {getStatusBadge(economicActivity.isActive)}
                  </td>
                  <td>
                    <div className="text-sm text-gray-900">
                      {new Date(economicActivity.createdAt).toLocaleDateString('pt-BR')}
                    </div>
                  </td>
                  <td>
                    <Can permission={P.CATALOG_ACCESS}>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => onView(economicActivity)}
                          className="btn btn-outline btn-sm btn-icon"
                          title="Visualizar"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => onEdit(economicActivity)}
                          className="btn btn-outline btn-sm btn-icon"
                          title="Editar"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => onDelete(economicActivity)}
                          className="btn btn-outline btn-sm btn-icon text-danger-600 hover:text-danger-700 hover:bg-danger-50"
                          title="Excluir"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </Can>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        <div className="pagination px-6 py-4 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Mostrando {((pagination.page - 1) * pagination.limit) + 1} a {Math.min(pagination.page * pagination.limit, total)} de {total} atividades
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={pagination.prevPage}
                disabled={!pagination.hasPrev}
                className="pagination-btn"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              
              {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                const page = i + 1;
                return (
                  <button
                    key={page}
                    onClick={() => pagination.goToPage(page)}
                    className={`pagination-btn ${pagination.page === page ? 'active' : ''}`}
                  >
                    {page}
                  </button>
                );
              })}
              
              {pagination.totalPages > 5 && (
                <>
                  <span className="px-2 text-gray-500">...</span>
                  <button
                    onClick={() => pagination.goToPage(pagination.totalPages)}
                    className="pagination-btn"
                  >
                    {pagination.totalPages}
                  </button>
                </>
              )}
              
              <button
                onClick={pagination.nextPage}
                disabled={!pagination.hasNext}
                className="pagination-btn"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EconomicActivityTable;
